import { Box } from "@mui/material"
import { type FC, useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { GoodsReceiptDetailsTotals, PurchaseOrderAddAlert } from "../../../../components"
import { useGoodsReceiptContext } from "../../../../contexts"
import type { IncomingPurchaseOrdersFormValues } from "../../schemas"
import { calculateIncomingPurchaseOrderFormTotals } from "../../utils"

import { IncomingPurchaseOrderForm } from "./IncomingPurchaseOrderForm"

import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

export const IncomingPurchaseOrdersTab: FC = () => {
  const { hasPermissions } = usePermissionManager()
  const { goodsReceipt, onAddPurchaseOrder, onRemovePurchaseOrder, isResettingRef } = useGoodsReceiptContext()
  const { watch } = useFormContext<IncomingPurchaseOrdersFormValues>()

  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const purchaseOrders = goodsReceipt.orders ?? []
  const formValues = watch()

  const totals = useMemo(() => {
    const lineFormValues = formValues.purchaseOrders.flatMap((purchaseOrder) => purchaseOrder.lines)
    return calculateIncomingPurchaseOrderFormTotals(lineFormValues)
  }, [formValues])

  if (!purchaseOrders.length && onAddPurchaseOrder) {
    return <PurchaseOrderAddAlert onAdd={onAddPurchaseOrder} />
  }

  return (
    <Box className='flex min-h-0 grow flex-col gap-y-8 overflow-auto' data-testid='goods-receipt-purchase-orders'>
      <Box className='flex flex-col gap-y-4'>
        {purchaseOrders.map((purchaseOrder, index) => (
          <IncomingPurchaseOrderForm
            key={purchaseOrder.orderId}
            purchaseOrder={purchaseOrder}
            purchaseOrderIndex={index}
            isResettingRef={isResettingRef}
            canMaintain={canMaintain}
            onRemove={onRemovePurchaseOrder}
          />
        ))}
      </Box>
      <GoodsReceiptDetailsTotals label='Estimated Total' totals={totals} />
    </Box>
  )
}
